import Vue from "vue"
import { get, union, uniq } from "lodash"
import moment from "moment"
import {
  getSessionRequestById,
  getActionableSessionRequests,
  acceptSessionRequest,
  rejectSessionRequest,
  finishSessionRequest,
} from "@/api/user/coaching/sessionRequests"
import { SESSION_REQUEST_STATUSES } from "@/constants/coaching"

const state = () => ({
  sessionRequestsMap: {},
  queueSessionRequestIds: [],
  activeSessionIds: [],
  reviewSessionIds: [],
  sessionIdInReview: null,
  requestRespondError: null,
  requestRespondErrorTimeoutFunction: null,
  requestPriceMap: {},
})

const mutations = {
  SAVE_SESSION_REQUEST: (state, sessionRequest) => {
    const sessionRequestId = sessionRequest.id
    Vue.set(state.sessionRequestsMap, sessionRequestId, sessionRequest)
  },
  UPDATE_SESSION_REQUEST_QUEUE: (state, sessionRequestIds) => {
    Vue.set(state, "queueSessionRequestIds", sessionRequestIds)
  },
  UPDATE_ACTIVE_SESSION_LIST: (state, sessionRequestIds) => {
    Vue.set(state, "activeSessionIds", sessionRequestIds)
  },
  UPDATE_SESSIONS_TO_REVIEW: (state, sessionRequestIds) => {
    Vue.set(state, "reviewSessionIds", sessionRequestIds)
  },
  SET_SESSION_IN_REVIEW: (state, sessionRequestId) => {
    Vue.set(state, "sessionIdInReview", sessionRequestId)
  },
  setRequestRespondError: (state, requestRespondError) =>
    (state.requestRespondError = requestRespondError),
  setRequestRespondErrorTimeoutFunction: (
    state,
    requestRespondErrorTimeoutFunction
  ) =>
    (state.requestRespondErrorTimeoutFunction = requestRespondErrorTimeoutFunction),
  setRequestPriceMap: (state, priceMap) => {
    Vue.set(state, "requestPriceMap", priceMap)
  },
}

const actions = {
  async fetchSessionRequest ({ commit, rootGetters }, requestId) {
    try {
      const response = await getSessionRequestById(
        rootGetters["User/Coaching/getInstaProAccessToken"],
        requestId
      )
      return get(response, "result", null)
    } catch (e) {
      console.error("Failed to fetch request with ID: " + requestId)
      return null
    }
  },
  async loadActionableSessionRequests ({ commit, getters, rootGetters }) {
    try {
      const response = await getActionableSessionRequests(
        rootGetters["User/Coaching/getInstaProAccessToken"]
      )

      const sessionRequests = get(response, ["result", "data"], [])

      let activeSessions = []
      let requestsInQueue = []
      let sessionsToReview = []

      sessionRequests.forEach(request => {
        // Convert times to moment objects
        request.created_at = moment.utc(request.created_at)
        request.updated_at = moment.utc(request.updated_at)

        commit("SAVE_SESSION_REQUEST", request)

        switch (request.status) {
          case SESSION_REQUEST_STATUSES.OPEN:
            requestsInQueue.push(request.id)
            break
          case SESSION_REQUEST_STATUSES.ACCEPTED:
            activeSessions.push(request.id)
            break
          case SESSION_REQUEST_STATUSES.FINISHED:
            if (request.report_card_required) {
              sessionsToReview.push(request.id)
            }
            break
        }
      })

      const requestQueueGetter = getters["getSessionRequestQueue"]
      commit(
        "UPDATE_SESSION_REQUEST_QUEUE",
        uniq(requestQueueGetter.concat(requestsInQueue))
      )

      const activeSessionsGetter = getters["getActiveSessions"]
      commit(
        "UPDATE_ACTIVE_SESSION_LIST",
        uniq(activeSessionsGetter.concat(activeSessions))
      )

      const sessionsToReviewGetter = getters["getSessionsToReview"]
      commit(
        "UPDATE_SESSIONS_TO_REVIEW",
        uniq(sessionsToReviewGetter.concat(sessionsToReview))
      )
    } catch (e) {
      console.error("Failed to load actionable session requests: ", e)
    }
  },
  saveSessionRequest ({ commit }, sessionRequest) {
    // Convert times to moment objects
    sessionRequest.created_at = moment.utc(sessionRequest.created_at)
    sessionRequest.updated_at = moment.utc(sessionRequest.updated_at)

    commit("SAVE_SESSION_REQUEST", sessionRequest)
  },
  addSessionRequestToQueue ({ state, commit }, sessionRequestId) {
    commit(
      "UPDATE_SESSION_REQUEST_QUEUE",
      union(state.queueSessionRequestIds, [sessionRequestId])
    )
  },
  removeSessionRequestFromQueue ({ state, commit }, sessionRequestId) {
    commit(
      "UPDATE_SESSION_REQUEST_QUEUE",
      state.queueSessionRequestIds.filter(
        queueRequestId => queueRequestId !== sessionRequestId
      )
    )
  },
  addActiveSessionRequest ({ state, commit }, sessionRequestId) {
    commit(
      "UPDATE_ACTIVE_SESSION_LIST",
      union(state.activeSessionIds, [sessionRequestId])
    )
  },
  removeActiveSessionRequest ({ state, commit }, sessionRequestId) {
    commit(
      "UPDATE_ACTIVE_SESSION_LIST",
      state.activeSessionIds.filter(
        queueRequestId => queueRequestId !== sessionRequestId
      )
    )
  },
  addSessionToReview ({ state, commit }, sessionRequestId) {
    commit(
      "UPDATE_SESSIONS_TO_REVIEW",
      union(state.reviewSessionIds, [sessionRequestId])
    )
  },
  removeSessionToReview ({ state, commit }, sessionRequestId) {
    commit(
      "UPDATE_SESSIONS_TO_REVIEW",
      state.reviewSessionIds.filter(
        queueRequestId => queueRequestId !== sessionRequestId
      )
    )
  },
  setSessionToReview ({ commit }, sessionRequestId) {
    commit("SET_SESSION_IN_REVIEW", sessionRequestId)
  },
  async acceptSessionRequest (
    { state, commit, dispatch, getters, rootGetters },
    sessionRequestId
  ) {
    await acceptSessionRequest(
      rootGetters["User/Coaching/getInstaProAccessToken"],
      sessionRequestId
    )

    const sessionRequestGetter = getters.getSessionRequest
    const sessionRequest = sessionRequestGetter(sessionRequestId)
    if (sessionRequest) {
      sessionRequest.updated_at = moment.utc()
      sessionRequest.status = SESSION_REQUEST_STATUSES.ACCEPTED
      commit("SAVE_SESSION_REQUEST", sessionRequest)
    }

    // Move request from in queue to active
    await dispatch("removeSessionRequestFromQueue", sessionRequestId)
    await dispatch("addActiveSessionRequest", sessionRequestId)
  },
  async rejectSessionRequest (
    { state, commit, dispatch, getters, rootGetters },
    { sessionRequestId, reason }
  ) {
    await rejectSessionRequest(
      rootGetters["User/Coaching/getInstaProAccessToken"],
      sessionRequestId,
      reason
    )

    const sessionRequestGetter = getters.getSessionRequest
    const sessionRequest = sessionRequestGetter(sessionRequestId)
    if (sessionRequest) {
      sessionRequest.updated_at = moment.utc()
      sessionRequest.status = SESSION_REQUEST_STATUSES.REJECTED
      commit("SAVE_SESSION_REQUEST", sessionRequest)
    }

    // Remove request from queue
    await dispatch("removeSessionRequestFromQueue", sessionRequestId)
  },
  async finishSessionRequest (
    { state, commit, dispatch, getters, rootGetters },
    sessionRequestId
  ) {
    const apiResponse = await finishSessionRequest(
      rootGetters["User/Coaching/getInstaProAccessToken"],
      sessionRequestId
    )

    const sessionRequestGetter = getters.getSessionRequest
    const sessionRequest = sessionRequestGetter(sessionRequestId)
    if (sessionRequest) {
      sessionRequest.updated_at = moment.utc(
        get(apiResponse, ["result", "updated_at"])
      )
      sessionRequest.status = SESSION_REQUEST_STATUSES.FINISHED
      commit("SAVE_SESSION_REQUEST", sessionRequest)
    }

    // Move request from active to review
    await dispatch("removeActiveSessionRequest", sessionRequestId)
    await dispatch("setSessionToReview", sessionRequestId)
    await dispatch("addSessionToReview", sessionRequestId)
  },
  async handleRequestRespondError ({ commit }, error) {
    await commit("setRequestRespondError", error)
    clearTimeout(this.requestRespondErrorTimeoutFunction)
    await commit(
      "requestRespondErrorTimeoutFunction",
      setTimeout(async () => {
        await commit("setRequestRespondError", null)
      }, 3000)
    )
  },
  updateRequestPriceMap ({ commit, state }, data) {
    const priceMap = state.requestPriceMap
    priceMap[data.request_id] = Number(data.price)
    priceMap[data.original_request_id] = Number(data.price)
    commit("setRequestPriceMap", priceMap)
  },
}

const getters = {
  getSessionRequest: state => requestId => state.sessionRequestsMap[requestId],
  getSessionRequestQueue: state =>
    state.queueSessionRequestIds
      .map(requestId => state.sessionRequestsMap[requestId])
      .filter(x => x),
  getActiveSessions: state =>
    state.activeSessionIds
      .map(requestId => state.sessionRequestsMap[requestId])
      .filter(x => x),
  getSessionsToReview: state =>
    state.reviewSessionIds
      .map(requestId => state.sessionRequestsMap[requestId])
      .filter(x => x),
  getSessionInReview: state =>
    state.sessionRequestsMap[state.sessionIdInReview],
  requestRespondError: state => state.requestRespondError,
  requestRespondErrorTimeoutFunction: state =>
    state.requestRespondErrorTimeoutFunction,
  requestPriceMap: state => state.requestPriceMap,
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/user/coaching/sessionRequests.js